import React from 'react';
import { FileText, AlertTriangle, Scale, Shield, Users, CheckCircle } from 'lucide-react';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-green-100 rounded-full">
              <FileText className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Use</h1>
          <p className="text-lg text-gray-600">
            Please read these terms carefully before using our platform
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="prose prose-lg max-w-none">
            {/* Emergency Disclaimer */}
            <div className="mb-8">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <div className="flex items-start">
                  <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-red-800 mb-3">Emergency Disclaimer</h3>
                    <p className="text-red-700 font-medium mb-4">
                      THERAPIST DIRECT DOES NOT PROVIDE MEDICAL OR MENTAL HEALTH SERVICES OR ADVICE.
                      IF YOU ARE EXPERIENCING A MEDICAL OR PSYCHIATRIC EMERGENCY, OR ARE THINKING ABOUT SUICIDE OR HARMING YOURSELF OR OTHERS, CALL 911 IMMEDIATELY.
                    </p>
                    <div className="text-red-700">
                      <p className="mb-2">Other resources include:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>National Suicide & Crisis Lifeline: Call or text 988 or visit 988lifeline.org</li>
                        <li>Crisis Text Line: Text 741741</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Introduction */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Scale className="h-6 w-6 text-green-600 mr-3" />
                Introduction
              </h2>
              <p className="text-gray-700 mb-4">
                These Terms of Use ("Terms") are a legal agreement between you ("Consumer," "you") and Therapist Direct, Inc. ("Therapist Direct," "we," "our," or "us").
              </p>
              <p className="text-gray-700 mb-4">
                Therapist Direct provides an online directory and best-match search tool to help you find mental health professionals and group practices (the "Services"). The Services are provided via our website and/or mobile applications (collectively, the "Platform").
              </p>
              <p className="text-gray-700">
                By accessing or using the Platform, you agree to be bound by these Terms and by our Privacy Policy. If you do not agree, you may not use the Platform.
              </p>
            </div>

            {/* Eligibility */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                Eligibility
              </h2>
              <p className="text-gray-700 mb-4">To use the Platform as a Consumer, you must:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Be at least 18 years old (or have parental/guardian consent if younger).</li>
                <li>Reside in the United States.</li>
                <li>Agree to these Terms.</li>
              </ul>
            </div>

            {/* Nature of Services */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="h-6 w-6 text-green-600 mr-3" />
                Nature of Services
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Therapist Direct is not a medical provider and does not give medical advice.</li>
                <li>The Platform offers informational listings and a "best match" tool based on criteria you provide (such as specialty, insurance, or availability).</li>
                <li>Search results are limited to practices and clinicians who choose to list on Therapist Direct.</li>
                <li>A match result is not a referral, endorsement, or guarantee of care. You are responsible for choosing your own provider.</li>
                <li>Any treatment relationship you form is strictly between you and the provider, not Therapist Direct.</li>
              </ul>
            </div>

            {/* Acceptable Use */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Acceptable Use</h2>
              <p className="text-gray-700 mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Use the Platform for unlawful or harmful purposes.</li>
                <li>Submit false or misleading information.</li>
                <li>Attempt to disrupt, hack, or misuse the Platform.</li>
                <li>Copy, scrape, or resell Platform content without permission.</li>
              </ul>
            </div>

            {/* Intellectual Property */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Intellectual Property</h2>
              <p className="text-gray-700">
                All content on the Platform (excluding provider-submitted listings) is owned by Therapist Direct or its licensors. You may use the Platform for personal, non-commercial purposes only.
              </p>
            </div>

            {/* Disclaimer of Warranties */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-6 w-6 text-green-600 mr-3" />
                Disclaimer of Warranties
              </h2>
              <p className="text-gray-700 mb-4">
                The Platform and all Services are provided "AS IS" and "AS AVAILABLE."
                Therapist Direct makes no warranties about:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Accuracy or completeness of listings.</li>
                <li>Suitability or safety of any provider.</li>
                <li>Availability, security, or reliability of the Platform.</li>
              </ul>
              <p className="text-gray-700 mt-4">
                You are solely responsible for verifying whether a provider is qualified, licensed, and appropriate for your needs.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">To the fullest extent permitted by law:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Therapist Direct shall not be liable for indirect, incidental, consequential, or punitive damages.</li>
                <li>Our total liability to you for any claim shall not exceed $100.</li>
              </ul>
            </div>

            {/* Indemnification */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Indemnification</h2>
              <p className="text-gray-700">
                You agree to defend, indemnify, and hold harmless Therapist Direct and its affiliates from any claims, damages, or expenses arising out of your misuse of the Platform or violation of these Terms.
              </p>
            </div>

            {/* Governing Law & Dispute Resolution */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Governing Law & Dispute Resolution</h2>
              <p className="text-gray-700 mb-4">
                These Terms are governed by the laws of the State of Wyoming, without regard to conflict of laws principles.
              </p>
              <p className="text-gray-700 mb-4">
                Any dispute, claim, or controversy arising out of or relating to these Terms or your use of the Platform shall be resolved exclusively through binding arbitration administered by the American Arbitration Association ("AAA") under its Commercial Arbitration Rules.
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Remote Arbitration Default:</strong> Arbitration shall be conducted remotely by default (e.g., by video conference), unless both parties agree in writing to an in-person proceeding.</li>
                <li><strong>Venue Flexibility:</strong> If an in-person arbitration is required, it shall be held in Dallas, Texas, unless the parties agree otherwise.</li>
                <li><strong>Final & Binding:</strong> The arbitrator's decision shall be final and binding, and judgment may be entered in any court of competent jurisdiction.</li>
                <li><strong>Class Action Waiver:</strong> All arbitration must be conducted on an individual basis. You and Therapist Direct waive the right to participate in any class or representative action.</li>
                <li><strong>One-Year Limitation:</strong> Any claim must be filed within one (1) year after it arises, or it is permanently barred.</li>
              </ul>
            </div>

            {/* Termination */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Termination</h2>
              <p className="text-gray-700">
                We may suspend or terminate your access to the Platform at any time, with or without cause.
              </p>
            </div>

            {/* Miscellaneous */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Miscellaneous</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>These Terms represent the entire agreement between you and Therapist Direct.</li>
                <li>If any provision is found invalid, the remainder will still apply.</li>
                <li>Our failure to enforce any right does not waive it.</li>
              </ul>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700">
                For questions about these Terms, please contact us at:
                <br />
                <a href="mailto:support@therapistdirect.com" className="text-green-600 hover:text-green-700">
                  support@therapistdirect.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
